import React, { useState, useEffect, useContext } from 'react';
import { Search, Upload, FileText, Image, Award, CreditCard, BookOpen, Briefcase, Download, Eye, Edit2, Trash2, Filter, Tag, Clock, Star, FolderOpen, Grid, List, Plus, X, Check, AlertCircle, ChevronDown, ChevronRight, Calendar, User, Share2, Lock, Unlock, Settings, Save, RefreshCw, Copy, Mail, Phone, Layers, CheckCircle, XCircle, Archive, Folder, ArrowUpWideNarrow, ArrowDownWideNarrow, RefreshCcwDotIcon } from 'lucide-react';
import styles from './UserContent.module.css';
import MultiUpload from './MultiUpload';
import { useParams } from 'react-router';
import { BASE_URL_API } from '../../References/urls';
import AuthContext from '../../store/auth-context';
import { Link } from 'react-router-dom';
import TagSelector from '../../Components/ManageUser/TagSelector';
import { toast } from 'react-toastify';
import CategorySelector from '../../Components/ManageUser/CategorySelector';

const UserContent = () => {
    const { userId } = useParams()
    const [userDetails, setUserDetails] = useState('')
    const authCtx = useContext(AuthContext)
    const masterTags = [
        { id: 1, label: "marksheets" },
        { id: 2, label: "aadhar card" },
        { id: 3, label: "passport" },
        { id: 4, label: "pan card" },
        { id: 5, label: "experience letter" },
        { id: 6, label: "salary slips" },
    ];

    const [isFetching, setIsFetching] = useState(false)
    const [sortOrder, setSortOrder] = useState("desc");
    // or "asc"

    const [config, setConfig] = useState({
        categories: [
            { id: 'identity', name: 'Identity Documents', icon: 'CreditCard', color: '#ec4899', enabled: true, required: false },
            { id: 'education', name: 'Education', icon: 'BookOpen', color: '#8b5cf6', enabled: true, required: false },
            { id: 'certificates', name: 'Certificates', icon: 'Award', color: '#10b981', enabled: true, required: false },
            { id: 'work', name: 'Work Samples', icon: 'Briefcase', color: '#f59e0b', enabled: true, required: false },
            { id: 'images', name: 'Portfolio Images', icon: 'Image', color: '#3b82f6', enabled: true, required: false },
            { id: 'other', name: 'Other', icon: 'FileText', color: '#6b7280', enabled: true, required: false }
        ],
        customFields: [
            { id: 'expiryDate', name: 'Expiry Date', type: 'date', enabled: true, categories: ['identity', 'certificates'] },
            { id: 'issueDate', name: 'Issue Date', type: 'date', enabled: true, categories: ['identity', 'certificates', 'education'] },
            { id: 'issuer', name: 'Issuing Authority', type: 'text', enabled: true, categories: ['identity', 'certificates', 'education'] },
            { id: 'documentNumber', name: 'Document Number', type: 'text', enabled: true, categories: ['identity'] },
            { id: 'verificationStatus', name: 'Verification Status', type: 'select', enabled: true, categories: ['all'], options: ['Pending', 'Verified', 'Rejected', 'Expired'] },
            { id: 'priority', name: 'Priority', type: 'select', enabled: true, categories: ['all'], options: ['Low', 'Medium', 'High', 'Critical'] }
        ],
        workflows: [
            { id: 'approval', name: 'Document Approval', enabled: true, stages: ['Submitted', 'Under Review', 'Approved', 'Rejected'] },
            { id: 'verification', name: 'Verification Process', enabled: true, stages: ['Unverified', 'In Progress', 'Verified', 'Failed'] }
        ],
        permissions: {
            canUpload: true,
            canDelete: true,
            canEdit:  false,
            canShare: false,
            canDownload: false,
            requireApproval: false,
            allowBulkOperations: true
        },
        uiSettings: {
            defaultView: 'list',
            itemsPerPage: 12,
            enableDarkMode: true,
            showThumbnails: true,
            enableDragDrop: true,
            compactMode: false
        },
        notifications: {
            emailOnUpload: false,
            emailOnApproval: false,
            emailOnExpiry: true,
            expiryWarningDays: 30
        },
        integrations: {
            cloudStorage: { enabled: false, provider: 'aws' },
            ocr: { enabled: false },
            autoTagging: { enabled: false },
            virusScanning: { enabled: true }
        },
        branding: {
            primaryColor: '#4f46e5',
            secondaryColor: '#3b82f6',
            logoUrl: '',
            companyName: 'Stylist CMS'
        }
    });

    const [documents, setDocuments] = useState([]);
    const [filteredDocs, setFilteredDocs] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [viewMode, setViewMode] = useState('grid');
    const [selectedDoc, setSelectedDoc] = useState(null);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [showConfigModal, setShowConfigModal] = useState(false);
    const [uploadForm, setUploadForm] = useState({
        name: '',
        category: 'identity',
        tags: '',
        notes: '',
        isPrivate: false,
        customFields: {}
    });
    const [sortBy, setSortBy] = useState('date');
    const [filterTags, setFilterTags] = useState([]);
    const [stats, setStats] = useState({});
    const [selectedDocs, setSelectedDocs] = useState([]);
    const [showBulkActions, setShowBulkActions] = useState(false);
    const [configTab, setConfigTab] = useState('categories');

    const iconMap = {
        CreditCard, BookOpen, Award, Briefcase, Image, FileText, FolderOpen, Folder, Archive, Layers
    };

    useEffect(() => {
        setFilteredDocs(documents);
        calculateStats(documents);
        setViewMode(config.uiSettings.defaultView);
    }, [documents]);

    const calculateStats = (docs) => {
        const newStats = { all: docs?.length };
        config.categories.filter(c => c.enabled)?.forEach(cat => {
            newStats[cat.id] = docs.filter(doc => doc.category === cat.id)?.length;
        });
        setStats(newStats);
    };

    useEffect(() => {
        let filtered = documents;

        if (selectedCategory !== 'all') {
            filtered = filtered.filter(doc => doc?.category === selectedCategory);
        }

        if (searchQuery) {
            filtered = filtered.filter(doc =>
                doc?.category?.toLowerCase().includes(searchQuery?.toLowerCase()) ||
                JSON.parse(doc?.tags)?.some(tag => tag?.toLowerCase()?.includes(searchQuery?.toLowerCase())) ||
                doc?.caption?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                Object.values(doc?.customFields || {})?.some(val =>
                    String(val)?.toLowerCase()?.includes(searchQuery?.toLowerCase())
                )
            );
        }

        if (filterTags?.length > 0) {
            filtered = filtered?.filter(doc =>
                filterTags?.some(tag => doc?.tags?.includes(tag))
            );
        }

        filtered = [...filtered].sort((a, b) => {
            let compare = 0;

            switch (sortBy) {
                case 'date': {
                    const aDate = new Date(a.createdOn);
                    const bDate = new Date(b.createdOn);
                    compare = aDate - bDate;  // ASC
                    break;
                }

                case 'name': {
                    const aName = a.category || "";
                    const bName = b.category || "";
                    compare = aName.localeCompare(bName); // ASC
                    break;
                }

                case 'size': {
                    const aSize = parseFloat(a.size || 0);
                    const bSize = parseFloat(b.size || 0);
                    compare = aSize - bSize; // ASC
                    break;
                }

                case 'priority': {
                    const priorityOrder = { Critical: 4, High: 3, Medium: 2, Low: 1 };
                    const prA = priorityOrder[a.customFields?.priority] || 0;
                    const prB = priorityOrder[b.customFields?.priority] || 0;
                    compare = prA - prB; // ASC
                    break;
                }

                default:
                    compare = 0;
            }

            // Apply ASC or DESC
            return sortOrder === "asc" ? compare : -compare;
        });



        setFilteredDocs(filtered);
    }, [documents, selectedCategory, searchQuery, sortBy, sortOrder, filterTags]);

    const toggleSortOrder = () => {
        setSortOrder(prev => (prev === "asc" ? "desc" : "asc"));
    };

    const getCategoryIcon = (category) => {
        const icons = {
            identity: '📄',
            education: '🎓',
            certificates: '🏆',
            work: '💼',
            images: '🖼️',
            other: '📋'
        };
        return icons[category] || '📄';
    };

    const toggleStar = (id) => {
        const updatedDocs = documents.map(doc =>
            doc.id === id ? { ...doc, starred: doc.starred === "1" ? "0" : "1" } : doc
        );
        setDocuments(updatedDocs);
    };

    const getRecruitContentCategories = async () => {
        const response = await fetch(BASE_URL_API + "/getRecruitContentCategories",
            {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    executiveIdentifier: authCtx.userIdentifier
                })
            })

        if (!response.ok) {
            console.log("Something went wrong : Server Error")
        } else {
            const respo = await response.json()
            if (respo.status === "success") {
                setConfig(prev => ({
                    ...prev,
                    categories: respo?.response
                }));
            } else {
                if (respo.message === "token Expired, please login again!") {
                    authCtx.logout()
                }
            }
            console.log(respo)
        }
    }

    useEffect(() => {
        getRecruitContentCategories()
    }, [])


    const deleteDocument = async (id) => {
        const contentToDelete = [id];
        console.log(contentToDelete)
        if (!config.permissions.canDelete) {
            alert('You do not have permission to delete documents');
            return;
        }
        if (window.confirm('Are you sure you want to delete this document?')) {
            const updatedDocs = documents.filter(doc => doc.id !== id);
            setDocuments(updatedDocs);
            calculateStats(updatedDocs);
            setShowPreviewModal(false);

            const response = await fetch(BASE_URL_API + "/deleteStylistContent",
                {
                    method: "POST",
                    body: JSON.stringify({
                        token: authCtx.token,
                        executiveIdentifier: authCtx?.userIdentifier,
                        contentIds: JSON.stringify(contentToDelete)
                    })
                })

            if (!response.ok) {
                console.log("Something went wrong : Server Error")
            } else {
                const respo = await response.json()
                if (respo.status === "success") {
                    toast("Content deleted successfully")
                } else {
                    if (respo?.message === "token Expired, please login again!") {
                        authCtx.logout()
                    } else {
                        console.log(respo)
                    }
                }
            }


        }
    };

    const togglePrivacy = (id) => {
        const updatedDocs = documents.map(doc =>
            doc.id === id ? { ...doc, isPrivate: doc.isPrivate === "1" ? "0" : "1" } : doc
        );
        setDocuments(updatedDocs);
    };

    const getAllTags = () => {
        if (!Array.isArray(documents) || documents.length === 0) return [];

        const tags = new Set();

        for (const doc of documents) {
            let rawTags = doc?.tags;
            let tagList = [];

            try {
                if (Array.isArray(rawTags)) {
                    tagList = rawTags;
                } else if (typeof rawTags === "string") {
                    // Try to parse JSON string (like '["tag1","tag2"]')
                    const parsed = JSON.parse(rawTags);
                    if (Array.isArray(parsed)) {
                        tagList = parsed;
                    } else {
                        // fallback if it's a comma-separated string
                        tagList = rawTags.split(",");
                    }
                } else if (rawTags != null) {
                    // Fallback: convert anything else to string and split by comma
                    tagList = String(rawTags).split(",");
                }
            } catch (err) {
                // JSON.parse failed — treat it as a comma-separated string
                tagList = typeof rawTags === "string" ? rawTags.split(",") : [];
            }

            tagList
                .map((tag) => tag?.trim())
                .filter((tag) => !!tag)
                .forEach((tag) => tags.add(tag.toLowerCase()));
        }

        return Array.from(tags);
    };


    const getThisTag = (k) => {
        let tagList = [];
        console.log(`this is type of : ${k}`, typeof k)
        return []
        if (typeof k === "string") {

            k = JSON.parse(k)
            tagList = k.split(",");
        } else if (Array.isArray(k)) {
            tagList = k;
        } else if (k != null) {
            // If it's something like an object, try to stringify and split
            tagList = String(k).split(",");
        }
        return tagList
    }

    const handleTagChange = (contentId, updatedTags) => {
        setDocuments((prevDocs) => {
            if (!Array.isArray(prevDocs)) return prevDocs;
            return prevDocs.map((doc) =>
                doc.id === contentId ? { ...doc, tags: updatedTags } : doc
            );
        });
    };

    const handleBulkDelete = async () => {
        if (!config.permissions.canDelete || !config.permissions.allowBulkOperations) {
            alert('You do not have permission for bulk operations');
            return;
        }
        if (window.confirm(`Delete ${selectedDocs?.length} selected documents?`)) {
            console.log(selectedDocs)
            const updatedDocs = documents.filter(doc => !selectedDocs.includes(doc.id));
            setDocuments(updatedDocs);
            calculateStats(updatedDocs);
            setSelectedDocs([]);
            setShowBulkActions(false);

            const response = await fetch(BASE_URL_API + "/deleteStylistContent",
                {
                    method: "POST",
                    body: JSON.stringify({
                        token: authCtx.token,
                        executiveIdentifier: authCtx?.userIdentifier,
                        contentIds: JSON.stringify(selectedDocs)
                    })
                })

            if (!response.ok) {
                console.log("Something went wrong : Server Error")
            } else {
                const respo = await response.json()
                if (respo.status === "success") {
                    toast("Content deleted successfully")
                } else {
                    if (respo?.message === "token Expired, please login again!") {
                        authCtx.logout()
                    } else {
                        console.log(respo)
                    }
                }
            }
        }
    };

    const handleBulkTag = (tag) => {
        const updatedDocs = documents.map(doc => {
            if (selectedDocs.includes(doc.id)) {
                return { ...doc, tags: [...new Set([...doc.tags, tag])] };
            }
            return doc;
        });
        setDocuments(updatedDocs);
        setSelectedDocs([]);
        setShowBulkActions(false);
    };

    const toggleDocSelection = (id) => {
        setSelectedDocs(prev =>
            prev.includes(id) ? prev.filter(docId => docId !== id) : [...prev, id]
        );
    };

    const saveConfiguration = () => {
        localStorage.setItem('cmsConfig', JSON.stringify(config));
        alert('Configuration saved successfully!');
    };

    const resetConfiguration = () => {
        if (window.confirm('Reset all configuration to defaults?')) {
            localStorage.removeItem('cmsConfig');
            window.location.reload();
        }
    };

    const exportConfiguration = () => {
        const dataStr = JSON.stringify(config, null, 2);
        const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
        const exportFileDefaultName = 'cms-config.json';
        const linkElement = document.createElement('a');
        linkElement.setAttribute('href', dataUri);
        linkElement.setAttribute('download', exportFileDefaultName);
        linkElement.click();
    };

    const getIconComponent = (iconName) => {
        return iconMap[iconName] || FolderOpen;
    };

    const getActiveCategories = () => {
        return [
            { id: 'all', name: 'All Documents', icon: 'FolderOpen', color: '#6366f1' },
            ...config.categories.filter(c => c.enabled)
        ];
    };

    const getCategoryFields = (categoryId) => {
        return config.customFields.filter(field =>
            field.enabled && (field.categories.includes('all') || field.categories.includes(categoryId))
        );
    };

    const getStylistDetails = async () => {
        const getStylistDetailsResponse = await fetch(BASE_URL_API + "/getStylistDetails",
            {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    stylist_id: userId
                })
            });

        if (!getStylistDetailsResponse.ok) {
            console.log("Something went wrong : Server Error");
        } else {
            const getStylistDetailsRespo = await getStylistDetailsResponse.json();
            if (getStylistDetailsRespo.status === 'success') {
                setUserDetails(getStylistDetailsRespo.response)
            } else {
                if (getStylistDetailsRespo.message === 'token Expired, please login again!') {
                    authCtx.logout()
                } else {
                    setUserDetails('')
                }
            }
            // console.log(getStylistDetailsRespo)
        }
    }

    const getStylistContentData = async () => {
        setIsFetching(true)
        const response = await fetch(BASE_URL_API + "/getStylistContentData",
            {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx?.token,
                    stylistIdentifier: userId
                })
            })

        if (!response.ok) {
            console.log("Something went wrong : Server Error")
        } else {
            const respo = await response.json()
            if (respo?.status === "success") {
                setDocuments(respo?.response)
            } else {
                if (respo?.message === "token Expired, please login again!") {
                    authCtx.logout()
                }
            }
        }
        setIsFetching(false)
    }

    useEffect(() => {
        getStylistDetails()
        getStylistContentData()
    }, [userId])

    const getTotalStorageUsed = () => {
        if (!documents || !Array.isArray(documents)) return "0 KB";
    
        // Total bytes
        const totalBytes = documents.reduce((sum, doc) => {
            return sum + (parseFloat(doc.size) || 0);
        }, 0);
    
        // Convert to best unit
        const KB = 1024;
        const MB = KB * 1024;
        const GB = MB * 1024;
    
        if (totalBytes >= GB) {
            return (totalBytes / GB).toFixed(2) + " GB";
        } else if (totalBytes >= MB) {
            return (totalBytes / MB).toFixed(2) + " MB";
        } else if (totalBytes >= KB) {
            return (totalBytes / KB).toFixed(2) + " KB";
        } else {
            return totalBytes + " Bytes";
        }
    };
    


    return (
        <div className={styles.container}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerContent}>
                    <div className={styles.headerLeft}>
                        <Link to={`/manage-users/grow/${userId}`} >
                            <div className={styles.title}>{userDetails?.stylist_name}'s CMS</div>
                            <p className={styles.subtitle}>{userDetails?.contact_number}</p>
                        </Link>
                    </div>
                    <div className={styles.headerActions}>
                        {/* <button onClick={() => setShowConfigModal(true)} className={styles.btnSecondary}>
                            <Settings size={20} />
                            Configure
                        </button> */}
                        {isFetching === true ?
                            <button title="Loading..." className={styles.btnSecondary}>
                                <RefreshCcwDotIcon size={20} className={styles.spinner} />
                            </button>
                            :
                            <button onClick={() => getStylistContentData()} className={styles.btnSecondary}>
                                <RefreshCcwDotIcon size={20} />
                            </button>
                        }
                        {config?.permissions?.canUpload && (
                            <button onClick={() => setShowUploadModal(true)} className={styles.btnPrimary}>
                                <Upload size={20} />
                                Upload
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className={styles.mainContent}>
                <div className={styles.layout}>
                    {/* Sidebar */}
                    <div className={styles.sidebar}>
                        <div className={styles.sidebarCard}>
                            <h2 className={styles.sidebarTitle}>
                                <Filter size={18} />
                                Categories
                            </h2>
                            <div className={styles.categoryList}>
                                {getActiveCategories().map(category => {
                                    const Icon = getIconComponent(category.icon);
                                    const count = stats[category.id] || 0;
                                    const isActive = selectedCategory === category.id;

                                    return (
                                        <button
                                            key={category.id}
                                            onClick={() => setSelectedCategory(category.id)}
                                            className={`${styles.categoryItem} ${isActive ? styles.categoryItemActive : ''}`}
                                            style={isActive ? { color: category.color } : {}}
                                        >
                                            <Icon size={20} style={{ color: category.color }} />
                                            <span className={styles.categoryName}>{category.name}</span>
                                            <span className={`${styles.badge} ${isActive ? styles.badgeActive : ''}`}>
                                                {count}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Tags Filter */}
                            <div className={styles.tagsSection}>
                                <h3 className={styles.sectionTitle}>
                                    <Tag size={18} />
                                    Filter by Tags
                                </h3>
                                <div className={styles.tagsList}>
                                    {getAllTags().map(tag => {
                                        const isActive = filterTags.includes(tag);
                                        return (
                                            <button
                                                key={tag}
                                                onClick={() => {
                                                    setFilterTags(prev =>
                                                        isActive ? prev.filter(t => t !== tag) : [...prev, tag]
                                                    );
                                                }}
                                                className={`${styles.tagButton} ${isActive ? styles.tagButtonActive : ''}`}
                                            >
                                                {tag}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Quick Stats */}
                            <div className={styles.statsSection}>
                                <h3 className={styles.sectionTitle}>Quick Stats</h3>
                                <div className={styles.statsList}>
                                    <div className={styles.statItem}>
                                        <span className={styles.statLabel}>Total Documents</span>
                                        <span className={styles.statValue}>{documents?.length}</span>
                                    </div>
                                    <div className={styles.statItem}>
                                        <span className={styles.statLabel}>Total Storage</span>
                                        <span className={styles.statValue}>{getTotalStorageUsed()}</span>
                                    </div>
                                    <div className={styles.statItem}>
                                        <span className={styles.statLabel}>Starred</span>
                                        <span className={`${styles.statValue} ${styles.statValueStarred}`}>
                                            {documents.filter(d => String(d.starred) === "1")?.length}
                                        </span>
                                    </div>
                                    <div className={styles.statItem}>
                                        <span className={styles.statLabel}>Private</span>
                                        <span className={`${styles.statValue} ${styles.statValuePrivate}`}>
                                            {documents.filter(d => String(d.isPrivate) === "1")?.length}
                                        </span>
                                    </div>
                                    <div className={styles.statItem}>
                                        <span className={styles.statLabel}>Verified</span>
                                        <span className={`${styles.statValue} ${styles.statValueVerified}`}>
                                            {documents.filter(d => d.customFields?.verificationStatus === 'Verified')?.length}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Main Content Area */}
                    <div className={styles.contentArea}>
                        {/* Search and Controls */}
                        <div className={styles.controlsCard}>
                            <div className={styles.controlsWrapper}>
                                <div className={styles.searchWrapper}>
                                    <Search className={styles.searchIcon} size={20} />
                                    <input
                                        type="text"
                                        placeholder="Search documents, tags, custom fields..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className={styles.searchInput}
                                    />
                                </div>

                                <select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    className={styles.sortSelect}
                                >
                                    <option value="date">Sort by Date</option>
                                    <option value="name">Sort by Name</option>
                                    <option value="size">Sort by Size</option>
                                    <option value="priority">Sort by Priority</option>
                                </select>

                                <div className={styles.viewToggle}>
                                    <button
                                        onClick={() => toggleSortOrder()}
                                        className={`${styles.viewButton} ${sortOrder === 'asc' ? styles.viewButtonActive : ''}`}
                                    >
                                        <ArrowUpWideNarrow size={20} />
                                    </button>
                                    <button
                                        onClick={() => toggleSortOrder()}
                                        className={`${styles.viewButton} ${sortOrder === 'desc' ? styles.viewButtonActive : ''}`}
                                    >
                                        <ArrowDownWideNarrow size={20} />
                                    </button>
                                </div>

                                <div className={styles.viewToggle}>
                                    <button
                                        onClick={() => setViewMode('grid')}
                                        className={`${styles.viewButton} ${viewMode === 'grid' ? styles.viewButtonActive : ''}`}
                                    >
                                        <Grid size={20} />
                                    </button>
                                    <button
                                        onClick={() => setViewMode('list')}
                                        className={`${styles.viewButton} ${viewMode === 'list' ? styles.viewButtonActive : ''}`}
                                    >
                                        <List size={20} />
                                    </button>
                                </div>

                                {config.permissions.allowBulkOperations && selectedDocs?.length > 0 && (
                                    <button
                                        onClick={() => setShowBulkActions(!showBulkActions)}
                                        className={styles.bulkButton}
                                    >
                                        Bulk Actions ({selectedDocs?.length})
                                    </button>
                                )}
                            </div>

                            {/* Bulk Actions Bar */}
                            {showBulkActions && selectedDocs?.length > 0 && (
                                <div className={styles.bulkActionsBar}>
                                    <div className={styles.bulkActionsContent}>
                                        <span className={styles.bulkActionsLabel}>{selectedDocs?.length} selected:</span>
                                        <button onClick={handleBulkDelete} className={styles.bulkActionDelete}>
                                            <Trash2 size={16} />
                                            Delete
                                        </button>
                                        <button onClick={() => handleBulkTag('important')} className={styles.bulkActionTag}>
                                            <Tag size={16} />
                                            Tag as Important
                                        </button>
                                        <button onClick={() => setSelectedDocs([])} className={styles.bulkActionClear}>
                                            Clear Selection
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Documents Display */}
                        {filteredDocs?.length === 0 ? (
                            <div className={styles.emptyState}>
                                <FileText size={64} className={styles.emptyIcon} />
                                <h3 className={styles.emptyTitle}>No documents found</h3>
                                <p className={styles.emptyText}>Upload your first document to get started</p>
                                {config.permissions.canUpload && (
                                    <button onClick={() => setShowUploadModal(true)} className={styles.btnPrimary}>
                                        <Upload size={20} />
                                        Upload Document
                                    </button>
                                )}
                            </div>
                        ) : viewMode === 'grid' ? (
                            <div className={styles.gridView}>
                                {filteredDocs.map(doc => {
                                    const category = config.categories.find(c => c.id === doc.category);
                                    const isSelected = selectedDocs.includes(doc.id);
                                    return (
                                        <div
                                            key={doc.id}
                                            className={`${styles.docCard} ${isSelected ? styles.docCardSelected : ''}`}
                                        >
                                            <div className={styles.docCardContent}>
                                                <div className={styles.docCardHeader}>
                                                    <div className={styles.docCardHeaderLeft}>
                                                        {config.permissions.allowBulkOperations && (
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleDocSelection(doc.id);
                                                                }}
                                                                className={styles.checkbox}
                                                            />
                                                        )}
                                                        <div className={styles.docThumbnail}><img src={doc?.url} /></div>
                                                    </div>
                                                    <div className={styles.docCardHeaderRight}>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleStar(doc.id);
                                                            }}
                                                            className={styles.starButton}
                                                        >
                                                            <Star
                                                                size={18}
                                                                className={doc.starred === "1" ? styles.starFilled : styles.starEmpty}
                                                            />
                                                        </button>
                                                        {doc.isPrivate === "1" ? (
                                                            <Lock size={18} className={styles.lockIcon} />
                                                        ) : (
                                                            <Unlock size={18} className={styles.unlockIcon} />
                                                        )}
                                                    </div>
                                                </div>
                                                <h3 className={styles.docTitle}>{doc.name}</h3>
                                                <div className={styles.docCategory}>
                                                    <span style={{ color: category?.color }}>●</span>
                                                    <span>{category?.name}</span>
                                                </div>
                                                <div className={styles.docMeta}>
                                                    <span>{parseFloat(parseFloat(parseInt(doc?.size) / 1024) / 1024).toFixed(1)}MB</span>
                                                    <span>{doc.version}</span>
                                                    <span>{doc?.createdOn}</span>
                                                </div>

                                                {doc.customFields?.verificationStatus && (
                                                    <div className={styles.docBadges}>
                                                        <span className={`${styles.statusBadge} ${styles['status' + doc.customFields.verificationStatus]}`}>
                                                            {doc.customFields.verificationStatus}
                                                        </span>
                                                    </div>
                                                )}

                                                {doc.customFields?.priority && (
                                                    <div className={styles.docBadges}>
                                                        <span className={`${styles.priorityBadge} ${styles['priority' + doc?.customFields?.priority]}`}>
                                                            {doc?.customFields?.priority} Priority
                                                        </span>
                                                    </div>
                                                )}

                                                <CategorySelector allCategories={config?.categories} />

                                                <TagSelector
                                                    masterTags={masterTags}
                                                    contentId={doc?.id}
                                                    selectedTags={doc?.tags}
                                                    onChange={handleTagChange}
                                                />
                                                {doc.caption && (
                                                    <p className={styles.docNotes}>{doc.caption}</p>
                                                )}
                                                <div className={styles.viewAction}>
                                                    <button
                                                        onClick={() => {
                                                            setSelectedDoc(doc);
                                                            setShowPreviewModal(true);
                                                        }}
                                                    >
                                                        View
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className={styles.listView}>
                                {filteredDocs.map((doc, index) => {
                                    const category = config.categories.find(c => c.id === doc.category);
                                    const isSelected = selectedDocs.includes(doc.id);
                                    return (
                                        <div
                                            key={doc.id}
                                            className={`${styles.listItem} ${isSelected ? styles.listItemSelected : ''} ${index !== 0 ? styles.listItemBorder : ''}`}

                                        >
                                            {config.permissions.allowBulkOperations && (
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        toggleDocSelection(doc.id);
                                                    }}
                                                    className={styles.checkbox}
                                                />
                                            )}
                                            <div className={styles.listItemThumbnail}> <img src={doc?.url} /></div>
                                            <div className={styles.listItemContent}>
                                                <div className={styles.listItemHeader}>
                                                    <h3 className={styles.listItemTitle}>{doc.name}</h3>
                                                    {doc.starred === "1" && <Star size={16} className={styles.starFilled} />}
                                                    {doc.isPrivate === "1" ? (
                                                        <Lock size={16} className={styles.lockIcon} />
                                                    ) : (
                                                        <Unlock size={16} className={styles.unlockIcon} />
                                                    )}
                                                    {doc.customFields?.verificationStatus && (
                                                        <span className={`${styles.statusBadge} ${styles['status' + doc.customFields.verificationStatus]}`}>
                                                            {doc.customFields.verificationStatus}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className={styles.listItemMeta}>
                                                    <span style={{ color: category?.color }}>● {category?.name}</span>
                                                    <span>{parseFloat(parseFloat(parseInt(doc?.size) / 1024) / 1024).toFixed(1)}MB</span>
                                                    <span>{doc?.createdOn}</span>
                                                    {doc.customFields?.priority && (
                                                        <span className={styles['priority' + doc.customFields.priority + 'Text']}>
                                                            {doc.customFields.priority}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className={styles.listItemTags}>
                                                <div>
                                                    <CategorySelector
                                                        allCategories={config?.categories}
                                                        selectedCategory={doc?.category}
                                                        contentIdentifier={doc?.id}
                                                    />
                                                    <TagSelector
                                                        masterTags={masterTags}
                                                        contentId={doc?.id}
                                                        selectedTags={doc?.tags}
                                                        onChange={handleTagChange}
                                                    />
                                                </div>
                                            </div>
                                            <div className={styles.viewAction}>
                                                <button
                                                    onClick={() => {
                                                        setSelectedDoc(doc);
                                                        setShowPreviewModal(true);
                                                    }}
                                                >
                                                    View
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Upload Modal */}
            {showUploadModal && (
                <div className={styles.modalOverlay}>
                    <div className={styles.closeBtnWrapper} >
                        <button onClick={() => setShowUploadModal(false)} >Close</button>
                    </div>
                    <MultiUpload />
                </div>
            )}

            {/* Preview Modal */}
            {showPreviewModal && selectedDoc && (
                <div className={styles.modalOverlay}>
                    <div className={`${styles.modal} ${styles.modalLarge}`}>
                        <div className={styles.modalHeader}>
                            <h2 className={styles.modalTitle}>{selectedDoc.name}</h2>
                            <button onClick={() => setShowPreviewModal(false)} className={styles.modalClose}>
                                <X size={24} />
                            </button>
                        </div>

                        <div className={styles.modalContent}>
                            <div className={styles.previewGrid}>
                                <div className={styles.previewItem}>
                                    <h3 className={styles.previewLabel}>Category</h3>
                                    <p className={styles.previewValue}>
                                        {config.categories.find(c => c.id === selectedDoc.category)?.name}
                                    </p>
                                </div>
                                <div className={styles.previewItem}>
                                    <h3 className={styles.previewLabel}>Upload Date</h3>
                                    <p className={styles.previewValue}>{selectedDoc?.createdOn}</p>
                                </div>
                                <div className={styles.previewItem}>
                                    <h3 className={styles.previewLabel}>File Size</h3>
                                    <p className={styles.previewValue}>{parseFloat(parseFloat(parseInt(selectedDoc?.size) / 1024) / 1024).toFixed(1)}MB</p>
                                </div>
                                <div className={styles.previewItem}>
                                    <h3 className={styles.previewLabel}>Version</h3>
                                    <p className={styles.previewValue}>v{selectedDoc.version}</p>
                                </div>
                                <div className={styles.previewItem}>
                                    <h3 className={styles.previewLabel}>Privacy</h3>
                                    <div className={styles.previewPrivacy}>
                                        {selectedDoc.isPrivate === "1" ? (
                                            <>
                                                <Lock size={16} className={styles.lockIcon} />
                                                <span className={styles.privateText}>Private</span>
                                            </>
                                        ) : (
                                            <>
                                                <Unlock size={16} className={styles.unlockIcon} />
                                                <span className={styles.publicText}>Public</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className={styles.previewItem}>
                                    <h3 className={styles.previewLabel}>Status</h3>
                                    <div className={styles.previewPrivacy}>
                                        {selectedDoc.starred === "1" && (
                                            <>
                                                <Star size={16} className={styles.starFilled} />
                                                <span className={styles.starredText}>Starred</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {selectedDoc.customFields && Object.keys(selectedDoc.customFields)?.length > 0 && (
                                <div className={styles.previewSection}>
                                    <h3 className={styles.previewSectionTitle}>Additional Information</h3>
                                    <div className={styles.previewGrid}>
                                        {Object.entries(selectedDoc.customFields).map(([key, value]) => {
                                            const field = config.customFields.find(f => f.id === key);
                                            if (!field || !value) return null;
                                            return (
                                                <div key={key} className={styles.previewItem}>
                                                    <p className={styles.previewLabel}>{field.name}</p>
                                                    <p className={styles.previewValue}>{value}</p>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {selectedDoc.workflowStatus && Object.keys(selectedDoc.workflowStatus)?.length > 0 && (
                                <div className={styles.previewSection}>
                                    <h3 className={styles.previewSectionTitle}>Workflow Status</h3>
                                    <div className={styles.workflowList}>
                                        {Object.entries(selectedDoc.workflowStatus).map(([workflowId, status]) => {
                                            const workflow = config.workflows.find(w => w.id === workflowId);
                                            if (!workflow || !workflow.enabled) return null;
                                            return (
                                                <div key={workflowId} className={styles.workflowBadge}>
                                                    <p className={styles.workflowName}>{workflow.name}</p>
                                                    <p className={styles.workflowStatus}>{status}</p>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {selectedDoc.caption && (
                                <div className={styles.previewSection}>
                                    <h3 className={styles.previewSectionTitle}>Caption</h3>
                                    <p className={styles.previewNotes}>{selectedDoc.caption}</p>
                                </div>
                            )}

                            <div className={styles.previewDisplay}>
                                <div className={styles.previewThumbnailLarge}>{selectedDoc.thumbnail}</div>
                                <p className={styles.previewDisplayLabel}>Document Preview</p>
                                <p className={styles.previewDisplayType}>{selectedDoc.type}</p>
                            </div>

                            <div className={styles.previewActions}>
                                {config.permissions.canDownload && (
                                    <button className={styles.actionButton}>
                                        <Download size={18} />
                                        Download
                                    </button>
                                )}
                                {config.permissions.canShare && (
                                    <button className={styles.actionButtonBlue}>
                                        <Share2 size={18} />
                                        Share
                                    </button>
                                )}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleStar(selectedDoc.id);
                                    }}
                                    className={styles.actionButtonYellow}
                                >
                                    <Star size={18} className={selectedDoc.starred === "1" ? styles.starFilled : ''} />
                                    {selectedDoc.starred === "1" ? 'Unstar' : 'Star'}
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        togglePrivacy(selectedDoc.id);
                                    }}
                                    className={styles.actionButtonGray}
                                >
                                    {selectedDoc.isPrivate === "1" ? <Unlock size={18} /> : <Lock size={18} />}
                                    {selectedDoc.isPrivate === "1" ? 'Make Public' : 'Make Private'}
                                </button>
                                {config.permissions.canEdit && (
                                    <button className={styles.actionButtonGreen}>
                                        <Edit2 size={18} />
                                        Edit
                                    </button>
                                )}
                                {config.permissions.canDelete && (
                                    <button
                                        onClick={() => deleteDocument(selectedDoc.id)}
                                        className={styles.actionButtonRed}
                                    >
                                        <Trash2 size={18} />
                                        Delete
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Config Modal */}
            {showConfigModal && (
                <div className={styles.modalOverlay}>
                    <div className={`${styles.modal} ${styles.modalLarge}`}>
                        <div className={styles.modalHeader}>
                            <h2 className={styles.modalTitle}>System Configuration</h2>
                            <button onClick={() => setShowConfigModal(false)} className={styles.modalClose}>
                                <X size={24} />
                            </button>
                        </div>

                        <div className={styles.modalContent}>
                            <p className={styles.configMessage}>
                                Configuration panel available. Manage categories, custom fields, workflows, permissions, and more.
                            </p>

                            <div className={styles.configActions}>
                                <div className={styles.configActionsLeft}>
                                    <button onClick={exportConfiguration} className={styles.btnSecondary}>
                                        <Download size={18} />
                                        Export Config
                                    </button>
                                    <button onClick={resetConfiguration} className={styles.btnDanger}>
                                        <RefreshCw size={18} />
                                        Reset to Default
                                    </button>
                                </div>
                                <button onClick={saveConfiguration} className={styles.btnPrimary}>
                                    <Save size={18} />
                                    Save Configuration
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserContent